"use client";

import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import toast from "react-hot-toast";
import { Plus } from "lucide-react";
import { Button } from "@/ui";
import {
  RightColumn,
  SectionHeader,
  SectionContent,
} from "./styles/SharedStyles";
import ExperienceCard from "@components/shared/ExperienceCard";
import ReorderRoles from "@components/shared/modals/ReorderRoles";
import EditEducation from "../modals/EditEducation";
import { ProfileSkeleton } from "./skeletons";
import {
  useAddCandidateEducation,
  useUpdateCandidateEducation,
  useRemoveCandidateEducation,
  useReorderCandidateEducation,
} from "@lib/candidates/useEducationMutations";

const Education = ({ candidate, isImporting }) => {
  const [editingEducation, setEditingEducation] = useState(null);
  const [reorderOpen, setReorderOpen] = useState(false);

  const addEducation = useAddCandidateEducation();
  const updateEducation = useUpdateCandidateEducation();
  const removeEducation = useRemoveCandidateEducation();
  const reorderEducation = useReorderCandidateEducation();

  const education = candidate.education ?? [];

  const handleEditEducation = (edu) => {
    setEditingEducation(edu);
  };

  const handleAddEducation = () => {
    const toastId = toast.loading("Adding...");
    addEducation.mutate(
      {
        candidate,
        education: {
          course_name: "New education",
          institution: "Institution",
          year_completed: "",
          description: "",
        },
      },
      {
        onSuccess: (data) => {
          toast.dismiss(toastId);
          setEditingEducation(data.data.education);
        },
        onError: () => {
          toast.dismiss(toastId);
          toast.error("Failed to add education");
        },
      }
    );
  };

  const handleUpdateEducation = (updatedEducation) => {
    const toastId = toast.loading("Saving...");
    updateEducation.mutate(
      { candidate, education: updatedEducation },
      {
        onSuccess: () => {
          toast.dismiss(toastId);
        },
        onError: () => {
          toast.dismiss(toastId);
          toast.error("Failed to save");
        },
      }
    );
  };

  // ReorderRoles renders the {id, name, position, logo} shape it shares with
  // the Float creation flow, so map each qualification into that view and
  // translate the reordered result back into a plain id order.
  const reorderItems = education.map((edu) => ({
    id: edu.id,
    name: edu.institution,
    position: edu.course_name,
    logo: edu.logo || edu.company_logo,
  }));

  const handleReorderSave = (reorderedItems) => {
    const toastId = toast.loading("Saving changes...");
    reorderEducation.mutate(
      {
        candidate,
        order: reorderedItems.map((item) => item.id),
      },
      {
        onSuccess: () => {
          toast.dismiss(toastId);
        },
        onError: () => {
          toast.dismiss(toastId);
          toast.error("Failed to reorder education");
        },
      }
    );
  };

  const handleDeleteEducation = (educationId) => {
    const toastId = toast.loading("Removing education...");
    removeEducation.mutate(
      { candidate, educationId },
      {
        onSuccess: () => {
          toast.dismiss(toastId);
        },
        onError: () => {
          toast.dismiss(toastId);
          toast.error("Failed to remove education");
        },
      }
    );
  };

  return (
    <Box id="candidate-section-education">
      <SectionContent>
        <Box>
          <RightColumn hideBorder={true}>
            <SectionHeader>
              <Typography variant="h2">Education</Typography>
            </SectionHeader>
            <Box sx={{ display: "flex", flexDirection: "column", gap: "1.2rem", mt: 2 }}>
              {education.map((edu) => (
                <ExperienceCard
                  key={edu.id}
                  selected
                  hideCheckbox
                  logoSrc={edu.logo || edu.company_logo}
                  title={edu.institution}
                  subtitle={edu.course_name}
                  dateRange={edu.year_completed}
                  summary={edu.description}
                  onReorderClick={
                    education.length > 1 ? () => setReorderOpen(true) : undefined
                  }
                  reorderLabel="Re-order education"
                  onEdit={() => handleEditEducation(edu)}
                />
              ))}
              {isImporting && (
                <Box>
                  <ProfileSkeleton />
                </Box>
              )}
              <Button
                variant="tertiary"
                size="sm"
                noPadding
                startIcon={<Plus size={16} />}
                onClick={handleAddEducation}
                style={{ alignSelf: "flex-start" }}
              >
                Add education
              </Button>
            </Box>
          </RightColumn>
        </Box>
      </SectionContent>
      <EditEducation
        key={editingEducation?.id}
        open={editingEducation !== null}
        onClose={() => setEditingEducation(null)}
        education={editingEducation}
        onUpdate={handleUpdateEducation}
        onDelete={handleDeleteEducation}
        candidate={candidate}
      />
      <ReorderRoles
        open={reorderOpen}
        onClose={() => setReorderOpen(false)}
        roles={reorderItems}
        onSave={handleReorderSave}
        title="Re-order education"
        description="Drag qualifications up or down to reorder them — the new order will be reflected on the candidate's profile."
        successMessage="Education order updated"
      />
    </Box>
  );
};

export default Education;
